import CompetitionList from "../../../components/ActiveCompetitions/competitionList";
import { useCompetitionCategories } from "@/app/redux/features/categories/selectors";
import { CompetitionCategory } from "@/app/redux/features/categories/slice";
import { MutableRefObject, useEffect, useRef, useState } from "react";
import HomeCompetitionsLoader from "./homeCompetitionsLoader";
import RenderAdsByCategory from "@/app/(general-header)/home/homepageCompetitions/renderAdsByCategory";

// Card min-heights mirror CompetitionItem's `min-h-[323px] lg:min-h-[494px]`, so skeleton
// cells reserve the same vertical space through the grid's own column/gap rules (no JS pixel
// math) and revealing the real cards does not shift layout.
const CARD_PLACEHOLDER_CLASS = "min-h-[323px] lg:min-h-[494px]";

const RowWrapper = ({
  children,
  category,
  containerRef,
}: {
  children: React.ReactNode;
  category: CompetitionCategory;
  containerRef: MutableRefObject<HTMLDivElement>;
}) => {
  const gridRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const HeadingTag = category.type === "featured" ? "h1" : "h2";

  // Defer mounting the (heavy) CompetitionItem cards until the row nears the viewport. Rows
  // above/near the fold satisfy the rootMargin immediately, so only far-below rows are deferred
  // -- cutting first-paint work without affecting what the user actually sees. The cards live in
  // the already-loaded home bundle (no extra chunk fetch), so mounting on scroll is instant.
  useEffect(() => {
    if (visible) return;
    const el = gridRef.current;
    if (!el || typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return;
    }
    const observer = new IntersectionObserver(
      entries => {
        if (entries.some(entry => entry.isIntersecting)) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: "600px" }
    );
    observer.observe(el);

    // Force-mount immediately when the category menu navigates to this row so the smooth-scroll
    // lands on fully rendered content instead of a placeholder.
    const onNavigate = (e: Event) => {
      if ((e as CustomEvent).detail === category.type) setVisible(true);
    };
    window.addEventListener("scroll-to-category", onNavigate);
    window.addEventListener("scroll-by-category", onNavigate);

    return () => {
      observer.disconnect();
      window.removeEventListener("scroll-to-category", onNavigate);
      window.removeEventListener("scroll-by-category", onNavigate);
    };
  }, [visible, category.type]);

  const placeholderCount = category.competitions?.length ?? 0;

  return (
    <div className={"w-full mx-auto max-w-[1360px] px-2 md:px-5 relative flex flex-col gap-10"}>
      <RenderAdsByCategory type={category.type} />

      <div ref={containerRef} id={category.type} className="relative">
        <div className={"flex flex-col items-start gap-1 mb-4 lg:mb-6"}>
          <HeadingTag
            className={
              "text-dark-1 font-jost text-base font-bold leading-[22px] lg:text-2xl lg:leading-[30px] uppercase"
            }>
            {category.title}
          </HeadingTag>
          <p className={"text-secondary font-jost text-sm font-normal leading-5 lg:text-lg lg:leading-6"}>
            {category.subtitle}
          </p>
        </div>
        <div
          ref={gridRef}
          className="grid xs:grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 2lg:grid-cols-4 xs:gap-2 md:gap-4 justify-content-center">
          {visible
            ? children
            : Array.from({ length: placeholderCount }).map((_, index) => (
                <div key={index} className={CARD_PLACEHOLDER_CLASS} aria-hidden="true" />
              ))}
        </div>
      </div>
    </div>
  );
};

const HomepageCompetitions = () => {
  const competitionsCategories = useCompetitionCategories({
    excludeCategories: ["subscriberCompetitions"],
  });

  // Revive script injection lives in home/page.tsx (useDeferredReviveAds) — this component always
  // mounts inside the homepage, and a second #script_ad_server container here caused duplicate
  // script loads with a duplicated element id.
  return (
    <>
      {competitionsCategories.map(category => (
        <CompetitionList
          key={category.type}
          category={category}
          LoadingComponent={HomeCompetitionsLoader}
          WrapperComponent={RowWrapper}
        />
      ))}
    </>
  );
};

export default HomepageCompetitions;
